import * as AntdIcons from '@ant-design/icons';
import { Alert, Avatar, Badge, Button, Drawer, Dropdown, Layout, List, Menu, Space, Tabs, Tag, theme, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import logo from '../../assets/tamansari-white.png';
import { useAuth } from '../../contexts/AuthContext';
import { getMyReminders, getNotificationCounts, getNotifications, markNotificationAsRead } from '../../services/notificationService';
import { ApiMenuItem, FCMNotificationType, Notification, Reminder } from '../../types';
import { formatDateTime } from '../../utils/formatDate';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;
const { TabPane } = Tabs;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [notificationDrawerVisible, setNotificationDrawerVisible] = useState(false);

  // State for Notifications
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [notificationError, setNotificationError] = useState<string | null>(null);
  const [notificationPage, setNotificationPage] = useState(1);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(true);

  // State for Tasks (Reminders)
  const [tasks, setTasks] = useState<Reminder[]>([]);
  const [totalTaskCount, setTotalTaskCount] = useState(0);
  const [taskError, setTaskError] = useState<string | null>(null);
  const [taskPage, setTaskPage] = useState(1);
  const [hasMoreTasks, setHasMoreTasks] = useState(true);
  const [tasksInitialized, setTasksInitialized] = useState(false);


  const { user, logout, userMenu, hasPermission, propertyAreas } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { token: { colorBgContainer } } = theme.useToken();

  // --- Data Fetching ---

  // 1. Fetch counts for the header badge on initial load
  const fetchBadgeCounts = useCallback(async () => {
    try {
      const [countsResponse, remindersResponse] = await Promise.all([
        getNotificationCounts(),
        getMyReminders(1, 1) // Fetch 1 item just to get the total count from pagination
      ]);
      setUnreadNotificationCount(countsResponse.unread);
      if (remindersResponse.pagination) {
        setTotalTaskCount(remindersResponse.pagination.total_items);
      }
    } catch (error) {
      console.error("Failed to fetch badge counts:", error);
      // This is a background task, so we don't need to show a UI error
    }
  }, []);

  useEffect(() => {
    fetchBadgeCounts();
  }, [fetchBadgeCounts]);

  // 2. Fetch initial notifications when the drawer is opened for the first time
  const fetchInitialNotifications = useCallback(async () => {
    if (notifications.length > 0) return; // Don't refetch if we already have data

    setNotificationError(null);
    setHasMoreNotifications(true);
    setNotificationPage(1);

    try {
      const notifResponse = await getNotifications(1, 10);
      setNotifications(notifResponse.data);
      setNotificationPage(2);
      if (notifResponse.pagination && notifResponse.pagination.current_page >= notifResponse.pagination.total_pages) {
        setHasMoreNotifications(false);
      }
    } catch (error) {
      console.error("Failed to fetch initial notifications:", error);
      setNotificationError("Gagal memuat notifikasi.");
    }
  }, [notifications.length]);

  // Effect to trigger notification fetch when drawer opens
  useEffect(() => {
    if (notificationDrawerVisible) {
      fetchInitialNotifications();
    }
  }, [notificationDrawerVisible, fetchInitialNotifications]);


  const fetchMoreNotifications = async () => {
    if (!hasMoreNotifications) return;
    try {
      const notifResponse = await getNotifications(notificationPage, 10);
      setNotifications(prev => [...prev, ...notifResponse.data]);
      if (notifResponse.pagination && notifResponse.pagination.current_page >= notifResponse.pagination.total_pages) {
        setHasMoreNotifications(false);
      } else {
        setNotificationPage(prev => prev + 1);
      }
    } catch (error) {
      console.error("Failed to fetch more notifications:", error);
    }
  };

  // 3. Fetch initial tasks when the task tab is clicked for the first time
  const fetchInitialTasks = async () => {
    setTaskError(null);
    setHasMoreTasks(true);
    setTaskPage(1);
    try {
      const taskResponse = await getMyReminders(1, 10);
      setTasks(taskResponse.data);
      if (taskResponse.pagination) {
        setTotalTaskCount(taskResponse.pagination.total_items);
      }
      setTaskPage(2);
      if (taskResponse.pagination && taskResponse.pagination.current_page >= taskResponse.pagination.total_pages) {
        setHasMoreTasks(false);
      }
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
      setTaskError("Gagal memuat task.");
    } finally {
      setTasksInitialized(true);
    }
  };

  const fetchMoreTasks = async () => {
    if (!hasMoreTasks) return;
    try {
      const taskResponse = await getMyReminders(taskPage, 10);
      setTasks(prev => [...prev, ...taskResponse.data]);
      if (taskResponse.pagination && taskResponse.pagination.current_page >= taskResponse.pagination.total_pages) {
        setHasMoreTasks(false);
      } else {
        setTaskPage(prev => prev + 1);
      }
    } catch (error) {
      console.error("Failed to fetch more tasks:", error);
    }
  };

  const handleTabChange = (key: string) => {
    if (key === '2' && !tasksInitialized) {
      fetchInitialTasks();
    }
  };

  const getIcon = useCallback((iconName?: string) => {
    if (!iconName) {
      return <AntdIcons.AppstoreOutlined />;
    }
    const IconComponent = (AntdIcons as any)[iconName];
    if (IconComponent) {
      return <IconComponent />;
    }
    return <AntdIcons.AppstoreOutlined />;
  }, []);

  const menuItems = React.useMemo(() => {
    const filterMenuByPermissions = (items: ApiMenuItem[]): ApiMenuItem[] => {
      return items.reduce((acc: ApiMenuItem[], item) => {
        // If the user has permission for this item, or if the item has no specific permission required
        const hasAccess = !item.permission || hasPermission(item.permission);

        if (item.children && item.children.length > 0) {
          // If it's a parent menu, filter its children
          const filteredChildren = filterMenuByPermissions(item.children);
          // Keep the parent if it has any visible children, regardless of its own permission
          if (filteredChildren.length > 0) {
            acc.push({ ...item, children: filteredChildren });
          }
        } else if (hasAccess) {
          // If it's a leaf menu, add it only if the user has access
          acc.push(item);
        }
        return acc;
      }, []);
    };

    const transformAndBuildMenu = (items: ApiMenuItem[]): any[] => {
      return items.map(item => {
        const menuItem: any = {
          key: item.path || item.key,
          icon: getIcon(item.icon),
          label: item.label,
          children: item.children && item.children.length > 0 ? transformAndBuildMenu(item.children) : undefined,
        };
        if (item.path && (!item.children || item.children.length === 0)) {
          menuItem.onClick = () => navigate(item.path as string);
        }
        return menuItem;
      });
    };

    const filteredMenu = filterMenuByPermissions(userMenu);
    return transformAndBuildMenu(filteredMenu);

  }, [userMenu, hasPermission, getIcon, navigate]);

  const profileMenuItems: any = [
    { key: 'profile', icon: <AntdIcons.UserOutlined />, label: 'Profile Settings', onClick: () => navigate('/profile/settings') },
    { type: 'divider' },
    { key: 'logout', icon: <AntdIcons.LogoutOutlined />, label: 'Logout', onClick: logout },
  ];

  const getSelectedKeys = () => {
    const currentPath = location.pathname;
    const allPaths = menuItems.flatMap(item => item.children ? [item, ...item.children] : [item])
      .map(item => item.key as string)
      .filter(key => key && key.startsWith('/'));
    const bestMatch = allPaths
      .filter(key => currentPath.startsWith(key))
      .sort((a, b) => b.length - a.length)[0];
    return bestMatch ? [bestMatch] : [];
  };

  const getOpenKeys = () => {
    const selectedKey = getSelectedKeys()[0];
    if (!selectedKey) return [];
    const parent = menuItems.find(item => item.children?.some((child: any) => child.key === selectedKey));
    return parent ? [parent.key] : [];
  };

  const renderPropertyAreaInfo = () => {
    if (!user) return null;
    const roleName = user.role?.name?.toLowerCase();
    if (roleName === 'admin') {
      return <Tag icon={<AntdIcons.GlobalOutlined />} color="success" style={{ marginRight: '16px' }}>Semua Area Properti</Tag>;
    }

    if (propertyAreas && propertyAreas.length > 0) {
      if (propertyAreas.length === 1) {
        return (
          <Tag icon={<AntdIcons.EnvironmentOutlined />} color="geekblue" style={{ marginRight: '16px' }}>
            {propertyAreas[0].name}
          </Tag>
        );
      }

      const menu = {
        items: propertyAreas.map(area => ({
          key: area.id,
          label: area.name,
          icon: <AntdIcons.HomeOutlined />,
        })),
      };

      return (
        <Dropdown menu={menu} trigger={['click']}>
          <Tag color="geekblue" style={{ marginRight: '16px', cursor: 'pointer' }}>
            <Space>
              <AntdIcons.EnvironmentOutlined />
              {propertyAreas[0].name}
              <AntdIcons.DownOutlined style={{ fontSize: '10px' }} />
            </Space>
          </Tag>
        </Dropdown>
      );
    }
    return null;
  };

  const handleNotificationClick = async (item: Notification) => {
    if (item.metadata.entity_type === 'lead' && item.metadata.entity_id) {
      navigate(`/leads/${item.metadata.entity_id}`);
    } else if (
      item.metadata.fcm_type === FCMNotificationType.LeadClosingSubmitted
      && item.metadata.lead_id) {
      navigate(`/leads/approvals/${item.metadata.lead_id}`);
    } else if (
      item.metadata.fcm_type === FCMNotificationType.LeadClosingApproved
      && item.metadata.lead_id) {
      navigate(`/leads/${item.metadata.lead_id}`);
    } else if (
      item.metadata.fcm_type === FCMNotificationType.LeadClosingRejected
      && item.metadata.lead_id) {
      navigate(`/leads/${item.metadata.lead_id}`);
    }

    setNotificationDrawerVisible(false);
    if (item.is_read) return;
    try {
      setNotifications(current => current.map(n => n.id === item.id ? { ...n, is_read: true } : n));
      setUnreadNotificationCount(current => Math.max(0, current - 1));
      await markNotificationAsRead(item.id);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleTaskClick = (item: Reminder) => {
    if (item.lead && item.lead.id) {
      navigate(`/leads/${item.lead.id}`);
    }
    setNotificationDrawerVisible(false);
  };

  const notificationContent = (
    <Tabs defaultActiveKey="1" onChange={handleTabChange}>
      <TabPane
        tab={
          <span>
            Notifikasi{' '}
            {unreadNotificationCount > 0 && (
              <Badge count={unreadNotificationCount} size="small" />
            )}
          </span>
        }
        key="1"
      >
        {notificationError ? (
          <div style={{ padding: '16px' }}><Alert message={notificationError} type="error" showIcon /></div>
        ) : (
          <div id="notificationScrollableDiv" style={{ height: 'calc(100vh - 180px)', overflow: 'auto' }}>
            <InfiniteScroll
              dataLength={notifications.length}
              next={fetchMoreNotifications}
              hasMore={hasMoreNotifications}
              loader={<div />}
              endMessage={<p style={{ textAlign: 'center', color: '#888', padding: '12px' }}><b>Sudah semua notifikasi ditampilkan</b></p>}
              scrollableTarget="notificationScrollableDiv"
            >
              <List
                dataSource={notifications}
                renderItem={item => (
                  <List.Item
                    onClick={() => handleNotificationClick(item)}
                    style={{ cursor: 'pointer', backgroundColor: item.is_read ? 'transparent' : '#e6f7ff', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
                  >
                    <List.Item.Meta
                      title={<Text strong={!item.is_read}>{item.title}</Text>}
                      description={item.message}
                      style={{ flex: 1 }}
                    />
                    <Text type="secondary" style={{ fontSize: '12px', whiteSpace: 'nowrap', marginLeft: '16px' }}>{formatDateTime(item.created_at)}</Text>
                  </List.Item>
                )}
                locale={{ emptyText: <div style={{ padding: '16px', textAlign: 'center' }}>Tidak ada notifikasi</div> }}
              />
            </InfiniteScroll>
          </div>
        )}
      </TabPane>
      <TabPane
        tab={
          <span>
            Task{' '}
            {totalTaskCount > 0 && (
              <Badge count={totalTaskCount} size="small" />
            )}
          </span>
        }
        key="2"
      >
        {taskError ? (
          <div style={{ padding: '16px' }}><Alert message={taskError} type="error" showIcon /></div>
        ) : (
          <div id="taskScrollableDiv" style={{ height: 'calc(100vh - 180px)', overflow: 'auto' }}>
            <InfiniteScroll
              dataLength={tasks.length}
              next={fetchMoreTasks}
              hasMore={hasMoreTasks}
              loader={<div />}
              endMessage={<p style={{ textAlign: 'center', color: '#888', padding: '12px' }}><b>Sudah semua task ditampilkan</b></p>}
              scrollableTarget="taskScrollableDiv"
            >
              <List
                dataSource={tasks}
                renderItem={item => (
                  <List.Item
                    onClick={() => handleTaskClick(item)}
                    style={{ cursor: 'pointer', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
                  >
                    <List.Item.Meta
                      title={item.title}
                      description={item.message}
                      style={{ flex: 1 }}
                    />
                    <Text type="secondary" style={{ fontSize: '12px', whiteSpace: 'nowrap', marginLeft: '16px' }}>{formatDateTime(item.reminder_time)}</Text>
                  </List.Item>
                )}
                locale={{ emptyText: <div style={{ padding: '16px', textAlign: 'center' }}>Tidak ada task</div> }}
              />
            </InfiniteScroll>
          </div>
        )}
      </TabPane>
    </Tabs>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        breakpoint="lg"
        onCollapse={(collapsed) => setCollapsed(collapsed)}
      >
        <div className="logo-container" style={{ height: '64px', margin: '16px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent' }}>
          <img src={logo} alt="Tamansari Logo" style={{ width: collapsed ? '40px' : '140px', height: 'auto', transition: 'width 0.3s ease-in-out' }} />
        </div>
        <Menu theme="dark" mode="inline" selectedKeys={getSelectedKeys()} defaultOpenKeys={getOpenKeys()} items={menuItems} />
      </Sider>
      <Layout>
        <Header style={{ padding: 0, background: colorBgContainer, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            type="text"
            icon={collapsed ? <AntdIcons.MenuUnfoldOutlined /> : <AntdIcons.MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: '16px', width: 64, height: 64 }}
          />
          <div style={{ display: 'flex', alignItems: 'center', marginRight: 24 }}>
            {renderPropertyAreaInfo()}
            <Badge count={unreadNotificationCount + totalTaskCount} size="small" offset={[-12, 8]}>
              <Button
                type="text"
                icon={<AntdIcons.BellOutlined style={{ fontSize: '18px' }} />}
                onClick={() => setNotificationDrawerVisible(true)}
                style={{ marginRight: 8 }}
              />
            </Badge>
            <Drawer
              title="Notifikasi & Task"
              placement="right"
              onClose={() => setNotificationDrawerVisible(false)}
              open={notificationDrawerVisible}
              width={400}
            >
              {notificationContent}
            </Drawer>
            <Dropdown menu={{ items: profileMenuItems }} placement="bottomRight" arrow>
              <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '0 12px', height: '100%' }}>
                <Avatar size="default" icon={<AntdIcons.UserOutlined />} src={user?.photo_url} />
                <div style={{ marginLeft: '12px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography.Text strong style={{ color: 'rgba(0, 0, 0, 0.88)', lineHeight: 1.3 }}>
                    {user?.full_name}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: '12px', lineHeight: 1.3 }}>
                    {user?.role?.name}
                  </Typography.Text>
                </div>
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ margin: '10px 16px', padding: 24, minHeight: 280, background: colorBgContainer, borderRadius: 6 }}>
          <Outlet />
        </Content>
        <Layout.Footer style={{ textAlign: 'center', padding: '12px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              Leads Management System ©{new Date().getFullYear()}
            </Typography.Text>
            {process.env.REACT_APP_VERSION && (
              <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                v{process.env.REACT_APP_VERSION}
              </Typography.Text>
            )}
          </div>
        </Layout.Footer>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
